/**
 * useCloudConnections Hook
 * Manages cloud storage connection state for all providers.
 */

import { useState, useCallback } from 'react';
import { rememberSameTabOAuth } from '@/utils/oauthPopup';
import { CloudStorageApi } from '../services/cloudStorageService';
import type { CloudConnection } from '../types';

export function useCloudConnections() {
  const [connections, setConnections] = useState<CloudConnection[]>([]);
  const [loading, setLoading] = useState(false);

  const loadConnections = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CloudStorageApi.listConnections();
      setConnections(data);
    } catch (error) {
      console.error('Error loading cloud connections:', error);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const connect = useCallback(async (provider: string) => {
    try {
      const { authorization_url, state } = await CloudStorageApi.startAuthorization(provider);
      // This tab goes to the provider and comes back to Settings, which
      // finishes only a state this tab noted (UserSettingsPage).
      rememberSameTabOAuth(state, provider);
      window.location.href = authorization_url;
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      throw error;
    }
  }, []);

  const disconnect = useCallback(async (provider: string) => {
    try {
      await CloudStorageApi.disconnect(provider);
      await loadConnections();
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      throw error;
    }
  }, [loadConnections]);

  const isConnected = useCallback((provider: string): boolean => {
    return connections.some(c => c.provider === provider && c.connection_status === 'active');
  }, [connections]);

  const getConnection = useCallback((provider: string): CloudConnection | undefined => {
    return connections.find(c => c.provider === provider && c.connection_status === 'active');
  }, [connections]);

  return {
    connections,
    loading,
    loadConnections,
    connect,
    disconnect,
    isConnected,
    getConnection,
  };
}
